import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface UpcomingRacePreview {
  race_id: string;
  race_number: number;
  category: string;
  distance: string;
  location: string;
  date: string;
  time: string;
  event_id: string;
}

export interface TopTipPreview {
  horse_name: string;
  jockey_name: string;
  tip_count: number;
}

export const useUpcomingRacePreview = () => {
  const { data: upcomingRace, isLoading: isLoadingRace } = useQuery({
    queryKey: ['upcoming-race-preview'],
    queryFn: async (): Promise<UpcomingRacePreview | null> => {
      const today = new Date().toISOString().split('T')[0];

      const { data: eventData, error: eventError } = await supabase
        .from('race_events')
        .select('id, location, date')
        .gte('date', today)
        .order('date', { ascending: true })
        .limit(1)
        .single();

      if (eventError || !eventData) return null;

      const { data: raceData, error: raceError } = await supabase
        .from('races')
        .select('id, race_number, category, distance, time')
        .eq('event_id', eventData.id)
        .eq('is_completed', false)
        .order('time', { ascending: true })
        .limit(1)
        .single();

      if (raceError || !raceData) return null;

      return {
        race_id: raceData.id,
        race_number: raceData.race_number,
        category: raceData.category,
        distance: raceData.distance,
        location: eventData.location,
        date: eventData.date,
        time: raceData.time,
        event_id: eventData.id,
      };
    },
  });

  const { data: topTips, isLoading: isLoadingTips } = useQuery({
    queryKey: ['top-tips-preview-event', upcomingRace?.event_id],
    queryFn: async (): Promise<TopTipPreview[]> => {
      if (!upcomingRace?.event_id) return [];

      // Get all upcoming races of this event
      const { data: races } = await supabase
        .from('races')
        .select('id')
        .eq('event_id', upcomingRace.event_id)
        .eq('is_completed', false);

      if (!races || races.length === 0) return [];

      const raceIds = races.map(r => r.id);

      const { data, error } = await supabase
        .from('horse_tips')
        .select(`
          participation_id,
          race_participations!inner(
            individual_horses!inner(name),
            individual_jockeys(name)
          )
        `)
        .in('race_id', raceIds);

      if (error || !data) return [];

      const tipCounts = data.reduce((acc, tip: any) => {
        const horseName = tip.race_participations?.individual_horses?.name || 'Neznámý kůň';
        const jockeyName = tip.race_participations?.individual_jockeys?.name || 'Neznámý žokej';
        const key = `${horseName}-${jockeyName}`;

        if (!acc[key]) {
          acc[key] = { horse_name: horseName, jockey_name: jockeyName, tip_count: 0 };
        }
        acc[key].tip_count++;
        return acc;
      }, {} as Record<string, TopTipPreview>);

      return Object.values(tipCounts)
        .sort((a, b) => b.tip_count - a.tip_count)
        .slice(0, 3);
    },
    enabled: !!upcomingRace?.event_id,
  });

  return {
    upcomingRace,
    topTips,
    isLoading: isLoadingRace || isLoadingTips,
  };
};
